import {AGENCY_KEYS, useAgencyInviteMutation, useAgencyQuery} from "@/api/agencyApi";
import {useCompanyQuery, useInviteCompanyMemberMutation} from "@/api/companyApi";
import UserIcon from "@/assets/dark_gradient/user.svg";
import CompanyLogoPlaceholder from "@/assets/images/company_logo_placeholder.svg";
import Button from "@/components/Common/Button";
import InputField from "@/components/Common/forms/InputField";
import PermissionSelect from "@/components/MultiAccount/PermissionSelect";
import ProfileSeparator from "@/pages/profile/ProfileSeparator";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {queryClient} from "@/queryClient";
import type {TPermission} from "@/store/types";
import {useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {z} from "zod";

const InviteMember: FC<{agencyId?: number | null; companyId?: number | null; hideHeadings?: boolean}> = ({
  agencyId,
  companyId,
  hideHeadings = false,
}) => {
  const {t} = useTranslation(["multi_account", "common"]);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [permission, setPermission] = useState<TPermission>("WRITE");

  const resetInvite = () => {
    setEmail("");
    setPermission("WRITE");
  };

  const isAgency = !!agencyId;
  const {data: agency} = useAgencyQuery({agencyId: agencyId ?? 0});
  const {data: company} = useCompanyQuery({companyId: companyId ?? 0});
  const logo =
    (isAgency ? (agency?.picture320 ?? agency?.picture) : (company?.picture320 ?? company?.picture)) ??
    CompanyLogoPlaceholder;
  const name = (isAgency ? agency?.name : company?.company_name) ?? "";

  const {mutate: inviteAgencyMember} = useAgencyInviteMutation({
    agencyId: agencyId!,
    email,
    mutationOptions: {
      onSuccess: () => {
        resetInvite();
        queryClient.invalidateQueries({queryKey: AGENCY_KEYS.qAgencyInvites(agencyId!)});
        capturePostHogEvent(PostHogEvents.UserInvitedToAgency, {agencyId, email});
      },
    },
  });
  const {mutate: inviteCompanyMember} = useInviteCompanyMemberMutation({
    companyId: companyId!,
    email,
    onSuccessCallback: () => {
      toast.success(t("invitation_success", {email}));
      resetInvite();
      capturePostHogEvent(PostHogEvents.UserInvitedToCompany, {companyId, email, permission});
    },
  });

  const handleInvite = () => {
    if (!email || !permission) {
      return;
    }
    if (!z.email().safeParse(email).success) {
      setEmailError("invalidEmail");
      return;
    }

    isAgency ? inviteAgencyMember() : inviteCompanyMember(permission);
  };

  return (
    <>
      {!hideHeadings && (
        <div className="flex items-center gap-1.5">
          <img src={logo} className="border-soft-grey-500 size-10 shrink-0 rounded-full border" />
          <span className="font-medium">{name}</span>
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <span className="text-neutral-black text-xs">{t("invite_to", {name})}</span>
        <div className="flex flex-col gap-3 md:flex-row md:gap-2">
          <InputField
            type="email"
            value={email}
            onChange={value => {
              setEmailError(null);
              setEmail(value);
            }}
            error={emailError}
            onKeyDown={e => e.key === "Enter" && handleInvite()}
            placeholder="sally@refluenced.com"
            className="border-primary-light-solid h-10 px-3 py-2.5"
            dataTestId="invite-email-field"
          />
          {isAgency ? (
            <div className="border-primary-light-solid flex h-10 min-w-16 items-center justify-center rounded-md border text-xs">
              {t("common:admin")}
            </div>
          ) : (
            <PermissionSelect
              value={permission}
              onChange={value => setPermission(value as TPermission)}
              renderButtonClassName="h-10"
            />
          )}
          <Button
            onClick={handleInvite}
            buttonStyle="Gradient"
            dataTestId="invite-submit-button"
            isDisabled={!email || !permission}
            className="h-10 px-5 py-4">
            {t("invite")}
          </Button>
        </div>
      </div>
      {!hideHeadings && <ProfileSeparator text={t("users_with_access")} icon={UserIcon} />}
    </>
  );
};

export default InviteMember;
